import { Box, Button, Heading, Text, Stack, Grid, Center, Spinner } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
  useGetResourceSectionDocsQuery,
  useGetResourceSectionsQuery,
  useGetResourcesQuery,
} from '../lib/api/GraphAPI';
import DocumentCard from '../components/DocumentCard';
import { useAppDispatch } from '../lib/hooks';
import PaginationControls from '../components/PaginationControls';
import { addNewGraphView, setDocumentStructure } from '../lib/state/slices/ProjectStateSlice';
import { Node } from '../lib/api/APITypes';
import { getDefaultGraphView, graphFromData } from '../lib/GraphFactory';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toaster } from '../components/ui/toaster';
import { Tooltip } from '../components/ui/tooltip';
import SerializedGraph from 'graphology-types';
import StyledButton from '@/components/ui/styledButton';

export default function ResourcePage() {
  const dispatch = useAppDispatch();
  const [selectedResource, setSelectedResource] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResourceName, setSelectedResourceName] = useState('');
  const [selectedResourceDescription, setSelectedResourceDescription] = useState('');
  const [selectedSectionName, setSelectedSectionName] = useState('');
  const [selectedResourceSubheader, setSelectedResourceSubheader] = useState('');
  const [view, setView] = useState<View>('articles');
  const [sectionDocs, setSectionDocs] = useState<Node[]>([]);

  type View = 'articles' | 'sections' | 'documents';

  const ITEMS_PER_PAGE = 12;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resourceId } = useParams();

  // Fetch resource data for the selected resource node
  const {
    data: resources,
    error: resourceError,
    isLoading: resourcesLoading,
    isError: resourceIsError,
  } = useGetResourcesQuery();

  // Fetch the sections for the selected resource
  const {
    data: resourceSections,
    error: resourceSectionError,
    isLoading: sectionsLoading,
    isError: resourceSectionIsError,
  } = useGetResourceSectionsQuery(selectedResource);

  // Fetch the documents for the selected section
  const { data: sectionDocsRaw, isLoading: docsLoading } = useGetResourceSectionDocsQuery(
    selectedSection ? { id: selectedSection, level: 1 } : skipToken,
  );

  const {
    docs: paginatedDocs,
    total: totalDocs,
    pages: totalPages,
  } = usePaginatedSectionDocs(sectionDocs, ITEMS_PER_PAGE, currentPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle initial route parameters and initialize the view state
  useEffect(() => {
    if (resourceId && resourceId !== selectedResource) {
      setSelectedResource(resourceId);
      setView('sections');

      // Reset section when resource changes
      if (selectedResource !== '' && selectedResource !== resourceId) {
        setSelectedSection('');
      }
    }
    const sectionParam = searchParams.get('section');
    if (sectionParam && resourceId) {
      // We'll set the actual section ID later when we have the sections data
      setSelectedSectionName(sectionParam);
      setView('documents');
    }
  }, [resourceId, searchParams]);

  // Set resource metadata when resources are loaded and we have a selected resource
  useEffect(() => {
    if (resources && selectedResource) {
      const resource = resources.find((r) => r.id === selectedResource);
      if (resource) {
        setSelectedResourceName(resource.name);
        setSelectedResourceDescription(resource.description);
        setSelectedResourceSubheader(resource.subheader);
      }
    }
  }, [resources, selectedResource]);

  // Find and set section ID when sections are loaded and we have a selected section name
  useEffect(() => {
    if (resourceSections && selectedSectionName && !selectedSection) {
      const section = resourceSections.find((s) => s.name === selectedSectionName);
      if (section) {
        setSelectedSection(section.id);
      }
    }
  }, [resourceSections, selectedSectionName]);

  // Process section documents when they're loaded
  useEffect(() => {
    if (sectionDocsRaw) {
      setSectionDocs(sectionDocsRaw.nodes);
    }
  }, [sectionDocsRaw]);

  useEffect(() => {
    if (resourceIsError) {
      toaster.create({
        title: 'Error fetching resource',
        description: 'Something went wrong while fetching the requested protocol/article',
        type: 'error',
        // duration: 3000,
        closable: true,
      });
    }
  }, [resourceIsError]);

  useEffect(() => {
    if (resourceSectionIsError) {
      toaster.create({
        title: 'Error fetching resource section',
        description:
          'Something went wrong while fetching the requested documents for the protocol/article',
        type: 'error',
        // duration: 3000,
        closable: true,
      });
    }
  }, [resourceSectionIsError]);

  const handleResourceClick = (resource: {
    id: string;
    name: string;
    description: string;
    subheader: string;
  }) => {
    setSelectedSection('');
    setCurrentPage(1);
    setView('sections');
    setSelectedResource(resource.id);
    setSelectedResourceName(resource.name);
    setSelectedResourceDescription(resource.description);
    setSelectedResourceSubheader(resource.subheader);
    navigate(`/resources/${resource.id}`);
  };

  const handleResourceSectionClick = (section: { id: string; name: string }) => {
    setSelectedSection(section.id);
    setSelectedSectionName(section.name);
    setCurrentPage(1);
    setView('documents');
    navigate(`/resources/${selectedResource}?section=${encodeURIComponent(section.name)}`);
  };

  const sortedResources = resources?.slice().sort((a, b) => {
    const extractTypeAndNumber = (name: string) => {
      const lower = name.toLowerCase();
      const isProtocol = lower.includes('protocol');
      const numberMatch = name.match(/\d+/);
      const number = numberMatch ? parseInt(numberMatch[0]) : 0;
      return { isProtocol, number };
    };

    const aData = extractTypeAndNumber(a.name);
    const bData = extractTypeAndNumber(b.name);

    if (aData.isProtocol !== bData.isProtocol) {
      return aData.isProtocol ? 1 : -1;
    }

    return aData.number - bData.number;
  });

  const isLoading =
    resourcesLoading || (selectedResource && sectionsLoading) || (selectedSection && docsLoading);

  return (
    <Box
      background="background"
      style={{
        width: '100%',
        height: '100%',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Header />

      <Box
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <Center h="200px">
            <Spinner size="xl" />
            <Text ml={4}>Loading content...</Text>
          </Center>
        ) : (
          <>
            {view === 'articles' && (
              <Grid padding={4} templateColumns={'repeat(auto-fit, minmax(380px, 1fr))'} gap={4}>
                {sortedResources?.map((resource: Resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onClick={handleResourceClick}
                    children={resource.subheader}
                  />
                ))}
                {(!sortedResources || sortedResources.length === 0) && (
                  <Text textAlign="center" color="gray.500">
                    No resources available.
                  </Text>
                )}
              </Grid>
            )}

            {/* Resource Section Buttons */}
            {view === 'sections' && resourceSections && (
              <Box
                background="midCardBG"
                borderRadius="0.375rem"
                style={{
                  width: '100%',
                  padding: '20px',
                }}
              >
                <Text fontSize="lg" fontWeight={700} color="primaryText" m={2} mb={6}>
                  {selectedResourceSubheader || 'Loading...'}
                </Text>
                <Text fontSize="md" fontWeight={700} color="secondaryText" m={2}>
                  About the resource
                </Text>
                <Text fontSize="sm" color="secondaryText" m={2}>
                  {selectedResourceDescription || 'Loading resource description...'}
                </Text>
                <Stack width={'100%'} overflow={'clip'}>
                  <Text fontSize="md" fontWeight={700} color="secondaryText" m={2}>
                    Key themes
                  </Text>
                  {resourceSections && resourceSections.length > 0 ? (
                    resourceSections?.map((section: any) => (
                      <Tooltip key={section.id} content={section.name} showArrow openDelay={500}>
                        <StyledButton
                          key={section.id}
                          variant="outline"
                          text={section.name}
                          onClick={() =>
                            handleResourceSectionClick({ id: section.id, name: section.name })
                          }
                        />
                      </Tooltip>
                    ))
                  ) : (
                    <Text textAlign="center" color="gray.500" m={2}>
                      No sections available for this resource.
                    </Text>
                  )}
                </Stack>
              </Box>
            )}

            {/* Document Cards for the selected section */}
            {view === 'documents' && selectedSection && (
              <Box>
                <Box
                  height="-webkit-fit-content"
                  width="100%"
                  display="flex"
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  mb={4}
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                    {Math.min(currentPage * ITEMS_PER_PAGE, totalDocs)} of {totalDocs} documents
                  </Text>
                  {sectionDocsRaw && sectionDocsRaw.nodes.length > 0 && (
                    <Button
                      onClick={() => {
                        let defaultGraphView = getDefaultGraphView();
                        defaultGraphView.serializedGraph = graphFromData(
                          { nodes: sectionDocsRaw!.nodes, edges: sectionDocsRaw!.edges },
                          '',
                        ).export() as unknown as SerializedGraph;
                        defaultGraphView.name = `${selectedResourceName} - ${selectedSectionName}`;
                        dispatch(
                          setDocumentStructure({
                            type: 'GRAPH',
                            key: defaultGraphView.id,
                            value: defaultGraphView,
                          }),
                        );
                        dispatch(addNewGraphView(defaultGraphView));
                        navigate('/graphs/' + defaultGraphView.id);
                      }}
                    >
                      View All Documents in Graph
                    </Button>
                  )}
                </Box>
                <Box
                  height="-webkit-fit-content"
                  width="100%"
                  borderRadius="md"
                >
                  <Grid
                    padding={4}
                    templateColumns={'repeat(auto-fit, minmax(380px, 1fr))'}
                    gap={4}
                  >
                    {paginatedDocs && paginatedDocs.length > 0 ? (
                      paginatedDocs.map((neighbour: Node) => (
                        <DocumentCard key={neighbour.id} id={neighbour.id} />
                      ))
                    ) : (
                      <Text textAlign="center" color="gray.500">
                        No documents available in this section.
                      </Text>
                    )}
                  </Grid>
                </Box>
                {totalPages > 1 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

type Resource = { id: string; name: string; subheader: string; description: string };

function ResourceCard({
  resource,
  onClick,
  children,
}: {
  resource: Resource;
  onClick?: (resource: Resource) => void;
  children?: React.ReactNode;
}) {
  const handleClick = () => {
    if (onClick) {
      onClick(resource);
    }
  };

  return (
    <Box
      padding={4}
      width={'100%'}
      border="none"
      borderRadius="md"
      display="flex"
      flexDirection="column"
      alignItems="center"
      role="button"
      _hover={{ backgroundColor: 'cardHover', cursor: 'pointer' }}
      backgroundColor="cardDefault"
      onClick={handleClick}
    >
      <Heading color="secondaryText" as="h2" size="md" mb={2}>
        {resource.name || 'Resource'}
      </Heading>
      <Text color="primaryText">
        {children || 'Click to open the subsections of this article.'}
      </Text>{' '}
    </Box>
  );
}

function usePaginatedSectionDocs(docs: Node[], itemsPerPage: number, page: number) {
  const total = docs?.length || 0;
  const pages = Math.ceil(total / itemsPerPage);
  const paginated = docs?.slice((page - 1) * itemsPerPage, page * itemsPerPage) || [];

  return { docs: paginated, total, pages };
}

function Header() {
  return (
    <div style={{ width: '100%', padding: '20px' }}>
      <Text color="secondaryText" fontWeight={700} fontSize={'xx-large'} marginBottom={'14px'}>
        Human Rights Resources
      </Text>
      <Text color="secondaryText" fontSize={'medium'}>
        Below you can find resources on all articles and protocols from the{' '}
        <a style={{ textDecoration: 'underline' }} href="https://ks.echr.coe.int/web/echr-ks/home">
          ECHR Knowledge Sharing platform
        </a>
        . Here you can select a key theme from each resource and see what judgements are referenced
        in it.
        <strong>
          {' '}
          You can also open the cases in a graph to see the connections between them.
        </strong>
      </Text>
    </div>
  );
}
